import React, { useEffect, useRef, useState } from 'react';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import { Toast } from '../../../components/Common/Toast';
import NoItemsFound from '../../../components/NoItemsFound/NoItemsFound';
import TableHeader from '../../../components/Common/TableHeader';
import PaginationButtons from '../../../components/Common/Pagination';
import { convertTimestampToDateTime } from '../../../helper/helper';
import OrderDetailsTable from '../PhysicalBookOrder/components/OrderDetailsTable';
import FulfillmentStatusBadges from '../PhysicalBookOrder/components/FulfillmentStatusBadges';
import { fulfillmentStatusOptionLabel, GROUPED_STATUS_OPTIONS, orderTypeLabel, printPdfBadge, toneClass } from '../PhysicalBookOrder/helpers/orderDisplay';
import { OrderFulfillmentStatusEnum } from '../../../interfaces/order.interface';
import {
  fetchGlobalOrders,
  fetchGlobalOrderCounts,
  GlobalOrderRow,
  GlobalOrdersResult,
  OrderCounts,
  OrdersChip,
} from './services/GlobalOrders.service';
import { createPrintBatch, dispatchPrintBatch } from '../PrintBatches/services/PrintBatches.service';
import RefreshButton from '../../../components/Admin/RefreshButton';
import { printJobLabel, shipstationOrderUrl } from '../PhysicalBookOrder/helpers/fulfillment';

// The five display groups (chips read as the funnel). ready_to_dispatch/stuck stay valid as
// deep-link targets from the dashboard even though they have no pill here.
const CHIPS: Array<{ key: OrdersChip | ''; label: string }> = [
  { key: '', label: 'All' },
  { key: 'waiting', label: 'Waiting' },
  { key: 'needs_attention', label: 'Needs attention' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'shipped', label: 'Shipped' },
];

// Order-type tabs — the primary navigation. Labels come from orderTypeLabel so they never
// diverge from the row badges. subscription/image_credits are non-physical (no fulfillment
// lifecycle), so their tabs hide the funnel chips and show payment-oriented columns instead.
const TYPE_TABS: string[] = ['', 'book', 'pre_order_book', 'bulk_print', 'subscription', 'image_credits'];
const NON_PHYSICAL_TYPES = new Set(['subscription', 'image_credits']);

const formatAmount = (order: Record<string, any>): string => {
  const price = order?.price;
  if (price === undefined || price === null || price === '') return '—';
  const currency = String(order?.currency || 'usd').toUpperCase();
  const symbol = currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : '$';
  return `${symbol}${price}`;
};

const GlobalOrders: React.FC<{ initialChip?: OrdersChip | ''; initialStatus?: string }> = ({
  initialChip = '',
  initialStatus = '',
}) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<GlobalOrdersResult | null>(null);
  const [selected, setSelected] = useState<GlobalOrderRow | null>(null);
  const [checked, setChecked] = useState<Map<string, GlobalOrderRow>>(new Map());
  const [batchLoading, setBatchLoading] = useState(false);

  const [chip, setChip] = useState<OrdersChip | ''>(initialChip);
  const [status, setStatus] = useState(initialStatus);
  const [orderType, setOrderType] = useState('');
  const [country, setCountry] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [counts, setCounts] = useState<OrderCounts | null>(null);
  const [includeLegacy, setIncludeLegacy] = useState(false);

  const isNonPhysicalView = NON_PHYSICAL_TYPES.has(orderType);

  // Tab/chip badge counts. byType is global; byGroup is scoped to the active type, so it refetches
  // when the tab changes (cheap count() aggregations, so an extra call per tab switch is fine).
  // Keep the previous counts on a transient failure instead of blanking every badge.
  const loadCounts = async () => {
    const c = await fetchGlobalOrderCounts(orderType || undefined, includeLegacy);
    if (c) setCounts(c);
  };

  useEffect(() => {
    let active = true;
    fetchGlobalOrderCounts(orderType || undefined, includeLegacy).then((c) => {
      if (active && c) setCounts(c);
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderType, includeLegacy]);

  // The server always echoes the CONFIGURED cutoff (independent of the toggle), so this is pure derived state.
  const trackFrom = counts?.trackFrom ?? null;

  // Switching to a non-physical type clears the fulfillment filters (they'd match nothing) and
  // drops any pending batch selection.
  const handleType = (value: string) => {
    setOrderType(value);
    if (NON_PHYSICAL_TYPES.has(value)) {
      setChip('');
      setStatus('');
      setChecked(new Map());
    }
  };

  // Monotonic guard: rapid tab/chip/filter clicks overlap requests; only the NEWEST response may
  // set rows, or a slower stale response repaints the wrong tab's data over the right one.
  const loadSeq = useRef(0);
  const load = async (pageNumber: number) => {
    const seq = ++loadSeq.current;
    setLoading(true);
    try {
      const data = await fetchGlobalOrders({
        chip,
        status: status ? [status] : undefined,
        orderType: orderType || undefined,
        country: country.trim() || undefined,
        search: search.trim() || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        includeLegacy,
        pageNumber,
      });
      if (data && seq === loadSeq.current) {
        setResult(data);
        // Prune phantom selections: a row present on this page that is no longer batchable
        // (another admin batched it, book flipped not-print-ready) loses its checkbox — drop it,
        // or it's counted in the button, rejected by the server, and impossible to uncheck.
        // Off-page keys are KEPT: print batches (up to 200 orders) are assembled across pages.
        setChecked((prev) => {
          if (!prev.size) return prev;
          const pageRows = new Map(data.rows.map((r) => [rowKey(r), r]));
          const next = new Map<string, GlobalOrderRow>();
          let changed = false;
          prev.forEach((row, key) => {
            const fresh = pageRows.get(key);
            if (fresh && !batchable(fresh)) {
              changed = true;
              return;
            }
            next.set(key, fresh || row); // refresh stale row data when the row is on this page
            if (fresh && fresh !== row) changed = true;
          });
          return changed ? next : prev;
        });
      }
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  };

  const rowKey = (r: GlobalOrderRow) => `${r.ownerEmail}|${r.orderNumber}`;
  const batchable = (r: GlobalOrderRow) =>
    r.fulfillmentStatus === OrderFulfillmentStatusEnum.READY_TO_DISPATCH &&
    r.orderType !== 'bulk_print' &&
    !r.dispatchHold &&
    // Mirror the server's validateOrder: rows already in an open print job or without a print-ready
    // book get rejected at batch creation — don't offer checkboxes the dispatch will bounce.
    !r.printBatchId &&
    r.bookPrintReady !== false;

  // Release grouping (client-side, pagination-safe): children whose release parent is on this
  // page nest under it behind an expand toggle — like Print Jobs' orders. Children whose parent
  // is on another page (or filtered out) render as normal rows.
  const [expandedReleases, setExpandedReleases] = useState<Set<string>>(new Set());
  const toggleRelease = (orderNumber: string, kids: GlobalOrderRow[]) => {
    setExpandedReleases((prev) => {
      const next = new Set(prev);
      if (next.has(orderNumber)) {
        next.delete(orderNumber);
        // Collapsing hides the child rows — drop them from the selection too, or the print-job
        // button would count orders the admin can no longer see.
        setChecked((prevChecked) => {
          const nextChecked = new Map(prevChecked);
          kids.forEach((kid) => nextChecked.delete(rowKey(kid)));
          return nextChecked;
        });
      } else {
        next.add(orderNumber);
      }
      return next;
    });
  };
  const parentsOnPage = new Set(
    (result?.rows || []).filter((r) => r.orderType === 'bulk_print').map((r) => String(r.orderNumber)),
  );
  const childrenByParent = new Map<string, GlobalOrderRow[]>();
  const topLevelRows = (result?.rows || []).filter((r) => {
    // Buyer-side primaries (what the index lists for pooled purchases) don't carry
    // parentBulkPrintOrderNumber — but combined-era children carry shipmentOrderNumber, which IS
    // the release parent's number. Accept either so pooled releases group too.
    const shipmentNo =
      r.order?.shipmentOrderNumber && String(r.order.shipmentOrderNumber) !== String(r.orderNumber)
        ? r.order.shipmentOrderNumber
        : undefined;
    const parentNo = r.order?.parentBulkPrintOrderNumber ?? shipmentNo;
    if (parentNo && parentsOnPage.has(String(parentNo))) {
      childrenByParent.set(String(parentNo), [...(childrenByParent.get(String(parentNo)) || []), r]);
      return false;
    }
    return true;
  });
  const flatRows: Array<GlobalOrderRow & { __isChild?: boolean }> = topLevelRows.flatMap((r) => {
    const kids = r.orderType === 'bulk_print' ? childrenByParent.get(String(r.orderNumber)) || [] : [];
    if (!kids.length || !expandedReleases.has(String(r.orderNumber))) return [r];
    return [r, ...kids.map((kid) => ({ ...kid, __isChild: true }))];
  });

  const toggleChecked = (r: GlobalOrderRow) => {
    const next = new Map(checked);
    if (next.has(rowKey(r))) next.delete(rowKey(r));
    else next.set(rowKey(r), r);
    setChecked(next);
  };

  const handleCreateBatch = async () => {
    const rows = [...checked.values()];
    if (!rows.length) return;
    if (!window.confirm(`Create a print job with ${rows.length} order(s) and dispatch it to the printer now?`)) return;
    const { ok, batchId, rejected } = await createPrintBatch(
      rows.map((r) => ({ ownerEmail: r.ownerEmail || '', orderNumber: r.orderNumber })),
      setBatchLoading,
    );
    // One action end-to-end: dispatch immediately — but only when EVERY selected order made it in.
    // A partial batch stays a draft on Print Jobs so the admin can review the rejections instead
    // of unknowingly sending fewer orders to the printer than they confirmed.
    if (ok && batchId) {
      if (rejected?.length) {
        Toast(`Print job saved as draft — ${rejected.length} order(s) were rejected. Review it on Print Jobs.`, true);
      } else {
        await dispatchPrintBatch(batchId, setBatchLoading);
      }
    }
    if (ok) {
      setChecked(new Map());
      load(page);
      loadCounts();
    }
  };

  useEffect(() => {
    setPage(1);
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chip, status, orderType, includeLegacy]);

  const handlePage = (p: string | number) => {
    const target = +p;
    if (target > 0 && target <= (result?.totalPages || 1)) {
      setPage(target);
      load(target);
    }
  };

  if (selected) {
    return (
      <div>
        <button
          type="button"
          className="text-storyblue underline text-sm mb-3"
          onClick={() => {
            setSelected(null);
            load(page);
          }}
        >
          ← Back to orders
        </button>
        <h1 className="flex my-2 text-sm text-gray-600">Owner: {selected.ownerEmail || '—'}</h1>
        <OrderDetailsTable selectedOrderDetails={selected.order as any} email={selected.ownerEmail} />
      </div>
    );
  }

  return (
    <div>
      {/* Primary navigation: one tab per order type */}
      <div className="flex flex-wrap gap-1 border-b border-gray-200 mb-3">
        {TYPE_TABS.map((value) => {
          const count = counts?.byType[value];
          return (
            <button
              key={value || 'all'}
              type="button"
              onClick={() => handleType(value)}
              className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                orderType === value
                  ? 'border-storyblue text-storyblue'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {value === '' ? 'All' : orderTypeLabel(value)}
              {count !== undefined && (
                <span
                  className={`ml-2 rounded-full px-1.5 py-0.5 text-xs font-semibold ${
                    orderType === value ? 'bg-storyblue/10 text-storyblue' : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Fulfillment funnel chips — only meaningful for physical order types */}
      {!isNonPhysicalView && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {CHIPS.map((c) => {
            const count = counts?.byGroup[c.key];
            return (
              <button
                key={c.key || 'all'}
                type="button"
                onClick={() => setChip(c.key)}
                className={`px-3 py-1 rounded-full text-sm font-semibold border ${
                  chip === c.key ? 'bg-storyblue text-white border-storyblue' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                }`}
              >
                {c.label}
                {count !== undefined && (
                  <span className={`ml-1.5 text-xs font-semibold ${chip === c.key ? 'text-white/90' : 'text-gray-400'}`}>{count}</span>
                )}
              </button>
            );
          })}
          {/* Deep-link-only chips (ready_to_dispatch / stuck) have no pill above; show the active
              filter so it's visible and clearable via the All pill. */}
          {chip && !CHIPS.some((c) => c.key === chip) && (
            <span className="px-3 py-1 rounded-full text-sm font-semibold border bg-storyblue text-white border-storyblue">
              {chip.charAt(0).toUpperCase() + chip.slice(1).replace(/_/g, ' ')}
            </span>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2 mb-3">
        {!isNonPhysicalView && (
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-gray-300 rounded px-2 py-1.5 text-sm">
            <option value="">All statuses</option>
            {GROUPED_STATUS_OPTIONS.map((g) => (
              <optgroup key={g.group} label={g.group}>
                {g.statuses.map((s) => (
                  <option key={s} value={s}>
                    {fulfillmentStatusOptionLabel(s)}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        )}
        <input
          type="text"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          placeholder="Country"
          className="border border-gray-300 rounded px-2 py-1 text-sm w-32"
        />
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-sm" />
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-sm" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setPage(1);
              load(1);
            }
          }}
          placeholder="Order # or owner email"
          className="border border-gray-300 rounded px-2 py-1 text-sm w-52"
        />
        <button
          type="button"
          onClick={() => {
            setPage(1);
            load(1);
          }}
          className="bg-storyblue text-white rounded px-3 py-1.5 text-sm font-semibold"
        >
          Apply
        </button>
        <RefreshButton
          onClick={() => {
            load(page);
            loadCounts();
          }}
          loading={loading}
        />
        {checked.size > 0 && (
          <button
            type="button"
            disabled={batchLoading}
            onClick={handleCreateBatch}
            className="bg-storygreen text-white rounded px-3 py-1.5 text-sm font-semibold disabled:opacity-50"
          >
            Create & dispatch print job ({checked.size})
          </button>
        )}
        <span className="text-xs text-gray-500 ml-auto flex items-center gap-3">
          {trackFrom && (
            <label
              className="flex items-center gap-1 cursor-pointer select-none"
              title={`Orders placed before ${trackFrom} are hidden by default (orders-track-from-date setting)`}
            >
              <input type="checkbox" checked={includeLegacy} onChange={(e) => setIncludeLegacy(e.target.checked)} />
              Include legacy (before {trackFrom})
            </label>
          )}
          {result ? `${result.totalRecords} orders${result.truncated ? ' (truncated scan)' : ''}` : ''}
        </span>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : !result || result.rows.length === 0 ? (
        <NoItemsFound message="No orders match these filters" />
      ) : (
        <>
          <div className="w-full overflow-auto">
            <table className="min-w-full border border-gray-300">
              {TableHeader(
                isNonPhysicalView
                  ? ['Date', 'Order #', 'Owner', 'Plan', 'Credits', 'Amount', 'Payment', ' ']
                  : ['', 'Date', 'Order #', 'Owner', 'Item', 'Qty', 'Country', 'Status', 'Print job', 'Shipment', ' '],
              )}
              <tbody>
                {flatRows.map((r, index) => (
                  <tr key={`${r.ownerEmail}-${r.id}`} className={r.__isChild ? 'bg-blue-50/40' : index % 2 === 0 ? 'bg-gray-100' : ''}>
                    {isNonPhysicalView ? (
                      <>
                        <td className="py-2 text-left px-4 border-b whitespace-nowrap">{convertTimestampToDateTime(r.timestamp)}</td>
                        <td className="py-2 text-left px-4 border-b">{r.orderNumber || '—'}</td>
                        <td className="py-2 text-left px-4 border-b max-w-xs truncate" title={r.ownerEmail || ''}>
                          {r.ownerEmail || '—'}
                        </td>
                        <td className="py-2 text-left px-4 border-b">{r.order?.extraInfo?.plan || '—'}</td>
                        <td className="py-2 text-left px-4 border-b">{r.order?.extraInfo?.credits ?? r.quantity ?? '—'}</td>
                        <td className="py-2 text-left px-4 border-b whitespace-nowrap">{formatAmount(r.order)}</td>
                        <td className="py-2 text-left px-4 border-b">
                          <span className="inline-block rounded bg-gray-100 text-gray-700 px-2 py-0.5 text-xs font-semibold capitalize">
                            {r.order?.status || '—'}
                          </span>
                        </td>
                        <td className="py-2 text-center px-4 border-b">
                          <button
                            type="button"
                            className="bg-storygreen text-white font-semibold rounded-md px-3 py-1 text-xs"
                            onClick={() => setSelected(r)}
                          >
                            Open
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-2 text-center px-2 border-b">
                          {batchable(r) && (
                            <input
                              type="checkbox"
                              checked={checked.has(rowKey(r))}
                              onChange={() => toggleChecked(r)}
                              title="Select for print job"
                            />
                          )}
                        </td>
                        <td className="py-2 text-left px-4 border-b whitespace-nowrap">{convertTimestampToDateTime(r.timestamp)}</td>
                        <td className="py-2 text-left px-4 border-b">{r.orderNumber || '—'}</td>
                        <td className="py-2 text-left px-4 border-b max-w-xs truncate" title={r.ownerEmail || ''}>
                          {r.ownerEmail || '—'}
                        </td>
                        <td className="py-2 text-left px-4 border-b max-w-xs truncate" title={r.bookTitle || r.bookId || ''}>
                          {r.__isChild && <span className="text-storyblue mr-1">↳</span>}
                          <span className="inline-block rounded bg-storyblue/10 text-storyblue px-2 py-0.5 text-xs font-semibold mr-1">
                            {orderTypeLabel(r.orderType)}
                          </span>
                          {r.orderType === 'bulk_print' && (childrenByParent.get(String(r.orderNumber))?.length || 0) > 0 && (
                            <button
                              type="button"
                              onClick={() => toggleRelease(String(r.orderNumber), childrenByParent.get(String(r.orderNumber)) || [])}
                              className="text-storyblue text-xs font-bold mr-1 underline"
                              title="Show/hide the orders in this release"
                            >
                              {expandedReleases.has(String(r.orderNumber)) ? '▾' : '▸'}{' '}
                              {childrenByParent.get(String(r.orderNumber))!.length} orders
                            </button>
                          )}
                          {r.bookTitle || r.bookId || (r.orderType === 'bulk_print' ? r.order?.classroomName || '—' : '—')}
                        </td>
                        <td className="py-2 text-left px-4 border-b">{r.quantity ?? '—'}</td>
                        <td className="py-2 text-left px-4 border-b">{r.shippingCountry || '—'}</td>
                        <td className="py-2 text-left px-4 border-b">
                          <span className="inline-flex flex-col gap-0.5">
                            <FulfillmentStatusBadges status={r.fulfillmentStatus} actionNeeded={r.actionNeeded} />
                            {/* A paid order whose book can't print would stall silently at every
                                guard — make it a first-class list signal, not a detail-only fact. */}
                            {r.bookPrintReady === false &&
                              r.fulfillmentStatus !== OrderFulfillmentStatusEnum.SHIPPED &&
                              r.fulfillmentStatus !== OrderFulfillmentStatusEnum.CANCELED && (
                              <span
                                className="inline-block rounded px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-800 w-fit"
                                title={r.bookPrintReadyReasons?.join(', ') || 'Book incomplete'}
                              >
                                Book not print-ready
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="py-2 text-left px-4 border-b whitespace-nowrap">
                          <span className="inline-flex items-center gap-1">
                            {(() => {
                              const pdf = printPdfBadge(r.order);
                              return pdf ? (
                                <span
                                  className={`inline-block rounded px-1.5 py-0.5 text-xs font-semibold ${toneClass[pdf.tone]}`}
                                  title={pdf.title}
                                >
                                  {pdf.label}
                                </span>
                              ) : null;
                            })()}
                            {printJobLabel(r.order) ? (
                              <span className="inline-block rounded bg-gray-100 text-gray-700 px-2 py-0.5 text-xs font-semibold" title="Print job — the batch sent to the printer">
                                🖨 {printJobLabel(r.order)}
                              </span>
                            ) : (
                              printPdfBadge(r.order) ? null : '—'
                            )}
                          </span>
                        </td>
                        <td className="py-2 text-left px-4 border-b whitespace-nowrap">
                          {r.order?.shipstationOrderId ? (
                            <span className="inline-flex items-center gap-1">
                              <span
                                className="inline-block rounded bg-storyblue/10 text-storyblue px-2 py-0.5 text-xs font-semibold"
                                title={
                                  r.trackingNumber
                                    ? `Tracking: ${r.carrier ? `${r.carrier} · ` : ''}${r.trackingNumber}`
                                    : r.order?.shipmentOrderNumber && String(r.order.shipmentOrderNumber) !== String(r.orderNumber)
                                      ? `Combined classroom shipment — one package under release #${r.order.shipmentOrderNumber}`
                                      : 'ShipStation order (shipment)'
                                }
                              >
                                📦 {r.order.shipstationOrderId}
                              </span>
                              {shipstationOrderUrl(r.order?.shipmentOrderNumber ?? r.orderNumber) && (
                                <a
                                  href={shipstationOrderUrl(r.order?.shipmentOrderNumber ?? r.orderNumber)!}
                                  target="_blank"
                                  rel="noreferrer"
                                  title="Open in ShipStation"
                                  className="text-storyblue text-xs font-bold"
                                >
                                  ↗
                                </a>
                              )}
                            </span>
                          ) : r.trackingNumber ? (
                            <span className="text-xs" title={r.carrier ? `Carrier: ${r.carrier}` : 'Tracking (no ShipStation order)'}>
                              {r.trackingNumber}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="py-2 text-center px-4 border-b">
                          <button
                            type="button"
                            className="bg-storygreen text-white font-semibold rounded-md px-3 py-1 text-xs"
                            onClick={() => setSelected(r)}
                          >
                            Open
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(result.totalPages ?? 0) > 1 && (
            <PaginationButtons totalPages={result.totalPages} currentPage={page} handlePageChange={handlePage} />
          )}
        </>
      )}
    </div>
  );
};

export default GlobalOrders;
